/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { z } from 'zod';

import { EmailSchema } from './primitives';

/**
 * Clinic onboarding (Super Admin / Customer Success) — single atomic call that
 * creates the clinic, the first Clinic Admin user, and the active subscription
 * on the chosen plan. See `POST /sa/clinics/onboard`.
 */
export const OnboardClinicSchema = z.object({
  clinic: z.object({
    name: z.string().trim().min(2, 'Clinic name is too short').max(120),
    legalName: z
      .string()
      .trim()
      .min(2)
      .max(180)
      .optional()
      .or(z.literal(''))
      .transform((v) => (v === '' ? undefined : v)),
    contactEmail: EmailSchema,
    contactPhone: z
      .string()
      .trim()
      .regex(/^\+?[0-9 ()-]{7,20}$/, 'Enter a valid phone number'),
    address: z.object({
      line1: z.string().trim().min(2).max(180),
      line2: z.string().trim().max(180).optional(),
      city: z.string().trim().min(2).max(80),
      state: z.string().trim().min(2).max(80),
      postalCode: z.string().trim().min(3).max(12),
      country: z.string().trim().length(2, 'ISO 3166-1 alpha-2 country code'),
    }),
    gstin: z
      .string()
      .trim()
      .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Invalid GSTIN')
      .optional()
      .or(z.literal(''))
      .transform((v) => (v === '' ? undefined : v)),
  }),
  firstAdmin: z.object({
    fullName: z.string().trim().min(2).max(120),
    email: EmailSchema,
  }),
  planSlug: z.string().trim().min(1).max(48),
  /** Optional trial extension — defaults to the plan-configured trial length. */
  trialDays: z.number().int().nonnegative().max(180).optional(),
});
export type OnboardClinicInput = z.infer<typeof OnboardClinicSchema>;

/** Postal address (shared by onboarding + the clinic settings form). */
export const AddressSchema = z.object({
  line1: z.string().trim().min(2).max(180),
  line2: z.string().trim().max(180).optional(),
  city: z.string().trim().min(2).max(80),
  state: z.string().trim().min(2).max(80),
  postalCode: z.string().trim().min(3).max(12),
  country: z.string().trim().length(2, 'ISO 3166-1 alpha-2 country code'),
});

/**
 * Clinic Admin → Settings (Figma). Edits the clinic's own profile. Mirrors the
 * API `UpdateClinicProfileDto`; the same shape validates on the backend.
 */
export const UpdateClinicProfileSchema = z.object({
  name: z.string().trim().min(2, 'Clinic name is too short').max(120),
  legalName: z
    .string()
    .trim()
    .min(2)
    .max(180)
    .optional()
    .or(z.literal(''))
    .transform((v) => (v === '' ? undefined : v)),
  contactEmail: EmailSchema,
  contactPhone: z
    .string()
    .trim()
    .regex(/^\+?[0-9 ()-]{7,20}$/, 'Enter a valid phone number'),
  timezone: z.string().trim().min(1).max(60),
  gstin: z
    .string()
    .trim()
    .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Invalid GSTIN')
    .optional()
    .or(z.literal(''))
    .transform((v) => (v === '' ? undefined : v)),
  /** Clinic logo (URL); stored in clinic_settings.branding. */
  logoUrl: z.string().trim().url('Enter a valid URL').max(500).optional().or(z.literal('')),
  address: AddressSchema,
});
export type UpdateClinicProfileInput = z.infer<typeof UpdateClinicProfileSchema>;

/** Plan change. */
export const ChangePlanSchema = z.object({
  planSlug: z.string().trim().min(1).max(48),
  reason: z.string().trim().max(500).optional(),
  /** When true, prorate the change to the current billing period. */
  prorate: z.boolean().default(true),
});
export type ChangePlanInput = z.infer<typeof ChangePlanSchema>;

/** Suspension reason capture. */
export const SuspendClinicSchema = z.object({
  reason: z.string().trim().min(3).max(500),
});
export type SuspendClinicInput = z.infer<typeof SuspendClinicSchema>;

/**
 * Clinic Admin → Settings → Notifications (Figma). Event-channel toggles for the
 * clinic. Mirrors the API `UpdateNotificationPrefsDto` and the `notification_prefs`
 * jsonb column; the same shape validates on the backend.
 */
export const NotificationPrefsSchema = z.object({
  sessionReminders: z.boolean(),
  paymentAlerts: z.boolean(),
  progressReports: z.boolean(),
  systemUpdates: z.boolean(),
});
export type NotificationPrefsInput = z.infer<typeof NotificationPrefsSchema>;

/** Impersonation request — issued by Customer Success / Support staff only. */
export const StartImpersonationSchema = z.object({
  clinicId: z.string().uuid(),
  ticketId: z.string().trim().max(64).optional(),
  reason: z.string().trim().min(5, 'Reason is required').max(500),
});
export type StartImpersonationInput = z.infer<typeof StartImpersonationSchema>;
